import { forwardRef, useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import cn from "../../../utils/cn";
import Choices from '@unite-us/choices.js';
import Icon from "../../Display/Icon/Icon";
import LogoLoader from "../../Display/LogoLoader/LogoLoader";
import { debounce, get, isArray, isEmpty, isEqual, isFunction, isString, map } from 'lodash';
import { buildChoices, fieldProps, getPreferredProp, getSelectValue } from "../utils";
import { isKeyPressed } from "../../../utils/isKeyPressed";
import { HOME_KEY_CODE, END_KEY_CODE } from "../../../utils/Browser/keyCodes";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const SelectField = /*#__PURE__*/forwardRef((props, ref) => {
  const {
    autoFocus = false,
    className = '',
    clearable = false,
    dataTestElement = '',
    disabled_prop = false,
    hideError = false,
    hideHint = false,
    hideLabel = false,
    hint = '',
    id,
    inline = false,
    label = '',
    labelClassName = '',
    labelKey = 'label',
    loading = false,
    loadingText = 'Loading...',
    loadOnMount = false,
    loadOptions,
    maxSelectedChoices = -1,
    multiple = false,
    options = [],
    placeholder = '',
    position = 'auto',
    postLabelContent = null,
    primary = false,
    renderChoiceLimit = -1,
    required = false,
    resetScrollPosition = true,
    searchChoices = true,
    searchEnabled = true,
    searchPlaceholderValue = 'Search...',
    searchResultLimit = 4,
    shouldSort = false,
    searchThreshold = 0.3,
    style = {},
    valueKey = 'value',
    omitRequiredLabel = false,
    'data-testid': dataTestId
  } = props;
  const [asyncOptions, setAsyncOptions] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [disabled, setDisabled] = useState(disabled_prop);
  const [value, setNewValue] = useState(getPreferredProp(props, 'value'));
  const asyncOptionsRef = useRef(asyncOptions);
  const optionsRef = useRef(options);
  const skipSearchRef = useRef(false); // flag to omit onSearch in HOME/END
  const wrapperRef = useRef(null); // Reference for component container

  const hasError = getPreferredProp(props, 'invalid') && getPreferredProp(props, 'touched') && !isOpen;
  const error = getPreferredProp(props, 'error') || (required ? 'Required' : '');
  const name = getPreferredProp(props, 'name');
  const debounceSetIsOpen = debounce(setIsOpen, 50);
  const element = useRef(ref);
  const choices = useRef(null);
  const containerClass = cn('relative group', {
    'app-components-select-field': true,
    'app-components-form-field': true,
    'app-components-form-field--inline': inline,
    'app-components-form-field--has-error has-error': hasError,
    'app-components-form-field--disabled': disabled,
    'app-components-select-field--has-error has-error': hasError,
    'pointer-events-none': loading,
    'choices--multiple': multiple,
    'with-search-icon': searchEnabled
  }, className);
  const labelClass = cn({
    'text-xs text-dark-grey leading-4 tracking-wide font-heavy-font': true,
    'app-components-form-field__label': true,
    'app-components-form-field__label--required': required && !omitRequiredLabel,
    'sr-only': hideLabel,
    [labelClassName]: !isEmpty(labelClassName)
  });
  const shouldAutoSelect = () => {
    const {
      autoSelectValue
    } = props;
    return autoSelectValue && required && options.length === 1;
  };
  const logoLoaderClass = cn({
    'flex mx-auto h-full w-11/12': true,
    'absolute z-10 left-0 right-0': true,
    'border-t border-b border-action-blue': true,
    'bg-action-blue': primary
  });
  const selectClass = cn({
    hidden: shouldAutoSelect(),
    visibile: !loading
  });
  const chevronClass = cn('absolute top-2.5 right-0 mr-4 ml-3 w-3 my-auto pointer-events-none', {
    'text-medium-grey': !primary,
    hidden: shouldAutoSelect()
  });

  /**
   * Sets the list of options
   * @param { array } options - Options on the dropdown
   * @param { boolean } replace - default to true, passed to choices.js.  clear option and reset them.
   & @param { string } placeholder - placeholder text in the SelectField
   * @public
   */
  const setOptions = function (options_arg, placeholder_arg) {
    let replace = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : true;
    const new_choices = buildChoices(options_arg, valueKey, labelKey, multiple, placeholder_arg);
    choices.current.setChoices(new_choices, 'value', 'label', replace);
    setNewValue(value);
  };

  /**
   * Should the component be disabled or enabled
   * @param {boolean} disabled
   * @public
   */
  const toggleDisabled = disabled_arg => {
    if (disabled_arg) {
      choices.current.disable();
    } else {
      choices.current.enable();
    }
  };
  const updateChoicesValue = newValue => {
    const {
      newValueKey
    } = props;
    if (isEmpty(newValue)) {
      choices.current.removeActiveItems();
      if (!multiple && !isEmpty(placeholder)) {
        choices.current.setValueByChoice('');
      }
      return choices;
    }
    if (isString(newValue)) {
      return choices.current.setValueByChoice(newValue);
    }
    if (isArray(newValue)) {
      choices.current.removeActiveItems();
      return choices.current.setValueByChoice(map(newValue, val => get(val, newValueKey, val)));
    }
    return choices.current.setValueByChoice(get(newValue, newValueKey, ''));
  };
  const asyncCallback = () => response => {
    const async_options = get(response, 'options', []);
    setOptions(async_options);
    setAsyncOptions(async_options);
  };
  const onSearch = event => {
    // If the flag is active, omit onSearch
    if (skipSearchRef.current) {
      return;
    }
    if (isFunction(loadOptions)) {
      const search = get(event, 'detail.value', '');
      const result = loadOptions(search, asyncCallback());
      if (result instanceof Promise) {
        result.then(response => {
          const search_options = get(response, 'options', []);
          setOptions(search_options, placeholder);
          setAsyncOptions(search_options);
        });
      }
    }
  };
  const onChange = () => {
    const {
      forceObjectValue,
      shouldSearchOnChange,
      onChange: manualOnChange
    } = props;
    const rawValue = choices.current.getValue();
    const optionList = isEmpty(optionsRef.current) ? asyncOptionsRef.current : optionsRef.current;
    const newValue = getSelectValue(rawValue, valueKey, optionList, forceObjectValue);
    if (shouldSearchOnChange) {
      onSearch();
    }
    const reduxFormOnChange = getPreferredProp(props, 'onChange');
    if (isFunction(reduxFormOnChange)) {
      reduxFormOnChange(newValue);
    }
    if (isFunction(manualOnChange) && !isEqual(manualOnChange, reduxFormOnChange)) {
      manualOnChange(newValue);
    }
  };
  const onShowDropdown = event => {
    const reduxFormOnFocus = getPreferredProp(props, 'onFocus');
    const manualPropsOnFocus = get(props, 'onFocus');
    if (isFunction(reduxFormOnFocus)) {
      reduxFormOnFocus(event);
    }
    if (isFunction(manualPropsOnFocus) && reduxFormOnFocus !== manualPropsOnFocus) {
      manualPropsOnFocus(event);
    }
    if (props.onShowDropdown) {
      props.onShowDropdown();
    }
    debounceSetIsOpen(true);
  };
  const onHideDropdown = () => {
    const {
      forceObjectValue,
      shouldSearchOnHide
    } = props;
    const rawValue = choices.current.getValue();
    const optionList = isEmpty(asyncOptions) ? options : asyncOptions;
    const selectValue = getSelectValue(rawValue, valueKey, optionList, forceObjectValue);
    const onBlur = getPreferredProp(props, 'onBlur');
    if (props.onHideDropdown) {
      props.onHideDropdown();
    }
    debounceSetIsOpen(false);
    choices.current.clearInput();
    if (shouldSearchOnHide) {
      onSearch();
    }
    if (isFunction(onBlur)) {
      onBlur(selectValue);
    }
    // Adding clearTimeout closes dropdown w/o it the dropdown reopens in Internet Explorer.
    clearTimeout(debounceSetIsOpen(false));
  };
  const updateSearchPlaceholderValue = newSearchPlaceholderProp => {
    choices.current.config.searchPlaceholderValue = newSearchPlaceholderProp;
    choices.current.input.placeholder = newSearchPlaceholderProp;
  };

  // Handler for HOME, END y CTRL+A
  const handleKeyDown = event => {
    if (multiple && event.ctrlKey && event.key && event.key.toLowerCase() === 'a') {
      event.preventDefault();
      const allChoices = choices.current.store.getChoices();
      const selectedValues = choices.current.getValue(true);
      if (selectedValues.length === allChoices.length) {
        choices.current.removeActiveItems();
      } else {
        allChoices.forEach(choice => {
          if (!selectedValues.includes(choice.label.trim())) {
            choices.current._addItem(choice.label);
          }
        });
      }
      return;
    }

    // Find dropdown within the current component's wrapper
    const wrapper = wrapperRef.current;
    if (!wrapper) return;
    const dropdown = wrapper.querySelector('.choices__list--dropdown');
    if (!dropdown) {
      return;
    }
    const choicesList = Array.from(dropdown.querySelectorAll('[data-choice]'));
    const highlighted = dropdown.querySelector('.is-highlighted');
    if (isKeyPressed(event, 'Home', HOME_KEY_CODE)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      skipSearchRef.current = true;
      setTimeout(() => {
        skipSearchRef.current = false;
      }, 150);
      if (choicesList.length > 0) {
        const firstChoice = choicesList[0];
        if (highlighted) highlighted.classList.remove('is-highlighted');
        firstChoice.classList.add('is-highlighted');
        if (choices.current.input) {
          choices.current.input.setAttribute('aria-activedescendant', firstChoice.id);
        }
        firstChoice.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }
    } else if (isKeyPressed(event, 'End', END_KEY_CODE)) {
      event.preventDefault();
      event.stopImmediatePropagation();
      skipSearchRef.current = true;
      setTimeout(() => {
        skipSearchRef.current = false;
      }, 150);
      if (choicesList.length > 0) {
        const lastChoice = choicesList[choicesList.length - 1];
        if (highlighted) highlighted.classList.remove('is-highlighted');
        lastChoice.classList.add('is-highlighted');
        if (choices.current.input) {
          choices.current.input.setAttribute('aria-activedescendant', lastChoice.id);
        }
        lastChoice.scrollIntoView({
          behavior: 'smooth',
          block: 'end'
        });
      }
    }
  };

  // Isolated listener to update aria-activedescendant for this instance
  const updateActiveDescendant = () => {
    if (!choices.current || !choices.current.input || !wrapperRef.current) return;
    const dropdown = wrapperRef.current.querySelector('.choices__list--dropdown');
    const highlighted = dropdown ? dropdown.querySelector('.is-highlighted') : null;
    if (highlighted) {
      choices.current.input.setAttribute('aria-activedescendant', highlighted.id);
    } else {
      choices.current.input.removeAttribute('aria-activedescendant');
    }
  };

  /**
   * Sets the selected value(s)
   * @param {(string|object)} value
   * @public
   */
  const setValue = originalValue => {
    let auxValue = originalValue;
    if (shouldAutoSelect()) {
      if (multiple) {
        auxValue = [options[0]];
      } else {
        const [firstOption] = options;
        auxValue = firstOption;
      }
    }
    updateChoicesValue(auxValue);
    if (shouldAutoSelect()) {
      onChange();
    }
  };
  useEffect(() => {
    const wrapperEl = wrapperRef.current;
    if (isOpen && wrapperEl) {
      // Use event delegation on the wrapper to only affect this instance
      wrapperEl.addEventListener('keyup', updateActiveDescendant);
      wrapperEl.addEventListener('click', updateActiveDescendant);
    }
    return () => {
      if (wrapperEl) {
        wrapperEl.removeEventListener('keyup', updateActiveDescendant);
        wrapperEl.removeEventListener('click', updateActiveDescendant);
      }
    };
  }, [isOpen]);
  useEffect(() => {
    const elementRef = element.current;
    if (!element || !elementRef) return;
    const choicesInstance = new Choices(elementRef, {
      itemSelectText: '',
      loadingText,
      maxItemCount: maxSelectedChoices,
      placeholderValue: placeholder,
      position,
      removeItemButton: clearable,
      renderChoiceLimit,
      resetScrollPosition,
      searchChoices,
      searchEnabled,
      searchFields: ['label'],
      searchPlaceholderValue,
      searchResultLimit,
      shouldSort,
      silent: true,
      fuseOptions: {
        include: 'score',
        threshold: searchThreshold
      },
      callbackOnCreateTemplates(template) {
        const classNames = this.config.classNames;
        return {
          choice: data => {
            const baseClasses = [classNames.item, classNames.itemChoice];
            if (data.disabled) {
              baseClasses.push(classNames.itemDisabled);
            } else {
              baseClasses.push(classNames.itemSelectable);
            }
            if (data.placeholder) {
              baseClasses.push(classNames.placeholder);
            }
            const finalClasses = [...baseClasses, 'flex', 'gap-2', 'items-center', 'p-2', 'hover:bg-gray-50', 'cursor-pointer'].join(' ');
            return template(`
              <div
                id="${id}-choice-${data.id}"
                class="${finalClasses}"
                data-choice
                data-id="${data.id}"
                data-value="${data.value}"
                data-select-text="${this.config.itemSelectText}"
                ${!data.disabled ? 'data-choice-selectable' : 'data-choice-disabled aria-disabled="true"'}
                role="option"
                aria-selected="${data.selected ? 'true' : 'false'}"
                ${data.customProperties?.ariaLabel ? `aria-label="${data.customProperties.ariaLabel}"` : ''}
              >
                <label
                  class="
                    text-sm
                    text-text-blue
                    font-regular-font
                    tracking-normal
                    cursor-pointer
                  "
                >
                  ${data.label}
                </label>
              </div>
            `);
          }
        };
      }
    });
    choices.current = choicesInstance;
    if (choices.current && choices.current.input) {
      const {
        input
      } = choices.current;
      input.setAttribute('role', 'combobox');
      input.setAttribute('aria-autocomplete', 'both');
      input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');

      // Set additional ARIA attributes for multiple select
      if (multiple) {
        input.setAttribute('aria-multiselectable', 'true');
      }

      // Make sure listbox IDs are unique per instance
      const listbox = wrapperRef.current.querySelector('.choices__list--dropdown');
      if (listbox) {
        const listboxId = `${id}-listbox`;
        listbox.setAttribute('role', 'listbox');
        listbox.setAttribute('id', listboxId);
        listbox.setAttribute('data-testid', `${id}-dropdown-listbox`);
        input.setAttribute('aria-owns', listboxId);

        // Ensure list items have correct ARIA attributes
        const updateChoiceAriaAttributes = () => {
          const choices = listbox.querySelectorAll('[data-choice]');
          choices.forEach(choice => {
            if (!choice.getAttribute('role')) {
              choice.setAttribute('role', 'option');
            }
          });
        };

        // Initial setup of ARIA attributes
        updateChoiceAriaAttributes();

        // Add mutation observer to handle dynamically added options
        const observer = new MutationObserver(updateChoiceAriaAttributes);
        observer.observe(listbox, {
          childList: true,
          subtree: true
        });

        // Store the observer to disconnect it later
        elementRef._ariaObserver = observer;
      }
      input.addEventListener('keydown', handleKeyDown, true);
    }
    if (elementRef) {
      elementRef.addEventListener('change', onChange);
      elementRef.addEventListener('search', onSearch);
      elementRef.addEventListener('showDropdown', onShowDropdown);
      elementRef.addEventListener('hideDropdown', onHideDropdown);
    }
    if (loadOnMount && loadOptions) {
      onSearch({
        detail: {
          value: ''
        }
      });
    }

    // eslint-disable-next-line consistent-return
    return () => {
      if (elementRef) {
        elementRef.removeEventListener('change', onChange);
        elementRef.removeEventListener('search', onSearch);
        elementRef.removeEventListener('showDropdown', onShowDropdown);
        elementRef.removeEventListener('hideDropdown', onHideDropdown);

        // Disconnect MutationObserver if it exists
        if (elementRef._ariaObserver) {
          elementRef._ariaObserver.disconnect();
          delete elementRef._ariaObserver;
        }
      }
      if (choices.current && choices.current.input) {
        choices.current.input.removeEventListener('keydown', handleKeyDown, true);
      }
      if (choices.current) {
        choices.current.destroy();
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Is accessible in onChange when passed as ref, otherwise asyncOptions and options are always empty inside onChange
  useEffect(() => {
    asyncOptionsRef.current = asyncOptions;
  }, [asyncOptions]);

  // Apply consistent styling to placeholders
  useEffect(() => {
    if (wrapperRef.current) {
      const placeholderElements = wrapperRef.current.querySelectorAll('.choices__placeholder');
      placeholderElements.forEach(placeholderElement => {
        placeholderElement.style.color = '#808D9E';
        placeholderElement.style.opacity = '1';
      });
      const inputElements = wrapperRef.current.querySelectorAll('.choices__input');
      inputElements.forEach(inputElement => {
        inputElement.style.color = '#808D9E';
        inputElement.style.opacity = '1';
      });
    }
    // Update aria-expanded when isOpen changes
    if (choices.current && choices.current.input) {
      choices.current.input.setAttribute('aria-expanded', isOpen ? 'true' : 'false');
    }
  }, [isOpen]);
  useEffect(() => {
    optionsRef.current = options;
  }, [options]);
  useEffect(() => {
    const optionList = isEmpty(options) ? asyncOptions : options;
    setOptions(optionList, placeholder);
    updateSearchPlaceholderValue(searchPlaceholderValue);
    toggleDisabled(disabled);
    setValue(value);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [placeholder, options, searchPlaceholderValue, disabled, value]);
  useEffect(() => {
    if (shouldAutoSelect()) {
      setOptions(options, placeholder);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [options]);

  // Update the disabled state
  useEffect(() => {
    setDisabled(disabled_prop);
  }, [disabled_prop]);

  // Update the value
  useEffect(() => {
    const newValue = getPreferredProp(props, 'value');
    setNewValue(newValue);
  }, [props]);
  const handleToggle = e => {
    e.nativeEvent.stopImmediatePropagation();
    if (isOpen) {
      choices.current.hideDropdown();
      debounceSetIsOpen(false);
    } else {
      debounceSetIsOpen(true);
      choices.current.showDropdown();
    }
  };
  return /*#__PURE__*/_jsxs("div", {
    className: containerClass,
    style: style.container,
    ref: wrapperRef,
    children: [loading && /*#__PURE__*/_jsxs("div", {
      className: logoLoaderClass,
      children: [/*#__PURE__*/_jsx(LogoLoader, {
        id: `${id}-logo-loader`,
        height: 30,
        styleClass: "inline-block m-auto mr-0",
        color: "white"
      }), /*#__PURE__*/_jsx("header", {
        className: "text-sm text-white inline-block align-middle m-auto ml-2",
        children: "Loading..."
      })]
    }), /*#__PURE__*/_jsx("label", {
      htmlFor: id,
      className: labelClass,
      style: style.label,
      "aria-hidden": true,
      children: label
    }), postLabelContent, /*#__PURE__*/_jsxs("div", {
      className: "relative",
      children: [/*#__PURE__*/_jsx("div", {
        "aria-label": label,
        className: selectClass,
        "data-test-element": dataTestElement || `dropdown_${getPreferredProp(props, 'name')}`,
        children: /*#__PURE__*/_jsx("select", {
          autoFocus: autoFocus,
          "data-testid": dataTestId,
          id: id,
          multiple: multiple,
          name: getPreferredProp(props, 'name'),
          onBlur: getPreferredProp(props, 'onBlur'),
          onFocus: getPreferredProp(props, 'onFocus'),
          ref: element,
          disabled: disabled
        })
      }), !shouldAutoSelect() && /*#__PURE__*/_jsx(Icon, {
        icon: "ChevronArrow",
        color: primary ? 'white' : 'medium-grey',
        className: chevronClass,
        id: `${id}-chevron`,
        onClick: handleToggle,
        disabled: disabled,
        direction: isOpen ? 'up' : 'down',
        canReceiveFocus: false
      }), shouldAutoSelect() && /*#__PURE__*/_jsx("div", {
        className: "app-components-select-field__auto-selected",
        children: get(options, `[0].${labelKey}`)
      })]
    }), !hideHint && hint && /*#__PURE__*/_jsx("div", {
      className: "app-components-form-field__hint",
      style: style.hint,
      children: hint
    }), !hideError && hasError && error && /*#__PURE__*/_jsx("div", {
      className: "app-components-form-field__error",
      style: style.error,
      "data-test-visible": hasError,
      "data-testid": `${name || id || 'unnamed'}-error`,
      children: error
    })]
  });
});
SelectField.propTypes = {
  /** Should the input be focused on load? */
  autoFocus: PropTypes.bool,
  /** Should the value be auto-selected when only one option available? */
  autoSelectValue: PropTypes.bool,
  /** className for reference */
  className: PropTypes.string,
  /** Show clear item button */
  clearable: PropTypes.bool,
  /** dataTestElement for qa testing */
  dataTestElement: PropTypes.string,
  /** Is the select disabled? */
  disabled_prop: PropTypes.bool,
  /** should the value be the object rather than the value string? */
  forceObjectValue: PropTypes.bool,
  /** Should the label be hidden? */
  hideLabel: PropTypes.bool,
  /** Remove Hint element */
  hideHint: PropTypes.bool,
  /** Remove Error element */
  hideError: PropTypes.bool,
  /** Help text displayed below the select */
  hint: PropTypes.node,
  /** id for reference */
  id: PropTypes.string.isRequired,
  /** Should the label be displayed inline with the select? */
  inline: PropTypes.bool,
  /** label text for button */
  label: PropTypes.node.isRequired,
  /** Key used for the label */
  labelKey: PropTypes.string,
  /**
   * Asynchronous options load function returning a Promise
   * @param {string} input - The search term
   */
  loadOptions: PropTypes.func,
  /** Calls loadOptions with an empty string on mount if true */
  loadOnMount: PropTypes.bool,
  /** The text that is shown while searching, defaults to empty string */
  loadingText: PropTypes.string,
  /** Is multiple select? */
  multiple: PropTypes.bool,
  /** Select options */
  options: PropTypes.array,
  /** Placeholder text */
  placeholder: PropTypes.string || PropTypes.node,
  /** Where the dropdown should appear in relation to the input */
  position: PropTypes.oneOf(['auto', 'top', 'bottom']),
  /** Content block that is placed between the label and the field */
  postLabelContent: PropTypes.node,
  /** How many options should be rendered? */
  renderChoiceLimit: PropTypes.number,
  /** Is the field required */
  required: PropTypes.bool,
  /** Whether scroll position should reset after adding an item?, defaults to true */
  resetScrollPosition: PropTypes.bool,
  /** False to disable Choices native search, defaults to true. */
  searchChoices: PropTypes.bool,
  /** If true, search is enabled */
  searchEnabled: PropTypes.bool,
  /** Placeholder for free text input */
  searchPlaceholderValue: PropTypes.string,
  /** Number of results to display */
  searchResultLimit: PropTypes.number,
  /** Should the options be automaticaly sorted? */
  shouldSort: PropTypes.bool,
  /** if true, search is triggered when selection is changed  */
  shouldSearchOnChange: PropTypes.bool,
  /** if true, search is triggered when hiding the drop down  */
  shouldSearchOnHide: PropTypes.bool,
  /** Limits number of selectable options to the value passed in */
  maxSelectedChoices: PropTypes.number,
  /** Override the inline-styles of the elements || **Not Recommended** */
  style: PropTypes.shape({
    container: PropTypes.object,
    label: PropTypes.object,
    input: PropTypes.object,
    hint: PropTypes.object,
    error: PropTypes.object
  }),
  /** Key used for the label */
  valueKey: PropTypes.string,
  /** Returns state of the dropdown display value */
  onShowDropdown: PropTypes.func,
  onHideDropdown: PropTypes.func,
  /** A threshold of 0.0 requires a perfect match (of both letters and location), a threshold of 1.0 would match anything.  */
  searchThreshold: PropTypes.number,
  'data-testid': PropTypes.string,
  /** See fieldProps doc */
  ...fieldProps
};
SelectField.displayName = 'SelectField';
export default SelectField;
//# sourceMappingURL=SelectField.js.map